import { Link } from "react-router-dom";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Grid3X3, Target, Zap, MessageCircle, Layers, User, Building2, Send, ArrowUpRight,
  Sparkles, Info,
} from "lucide-react";
import { contextLink, type ContextBlock } from "@/lib/context-links";

// ─────────────────────────────────────────────────────────────────────────────
// MessageContextPane — the "what the messaging matrix gathered + used" surface
// that sits on the LEFT of the wide dual-pane comms modal (message/reply on the
// right). Consolidates the former MessagingMatrixCard + PositioningCard into one
// scrollable rail that reads the generation_metadata seam (both the batch shape:
// messaging_variant/messaging_angle/role_type_used/matrix_source/
// enrichment_context, and the live-agent shape: painPointsUsed/valuePropsUsed/
// messagingAngle/matchScore/selectionRationale/model). Each block deep-links to
// where it's configured via the context-links registry (no link rendered when
// the client has no such screen yet).
//
// `mode` distinguishes "used" (a draft exists → generation_metadata) from
// "willUse" (pre-generate → inputs from classification / resolved-matrix preview)
// so the labels stay honest. (contact_modal.md §3.)
// ─────────────────────────────────────────────────────────────────────────────

interface MessageContextPaneProps {
  /** "used" = a draft exists (reads generation_metadata); "willUse" = pre-generate inputs. */
  mode?: "used" | "willUse";
  // Strategy (messaging matrix)
  messagingVariant?: string | null;
  messagingAngle?: string | null;
  roleTypeUsed?: string | null;
  accountCategory?: string | null;
  matrixSource?: string | null;
  enrichmentContext?: Record<string, any> | null;
  // Positioning & delivery
  messageType?: string | null;
  deliveryChannel?: string | null;
  senderName?: string | null;
  contactName?: string | null;
  contactTitle?: string | null;
  accountName?: string | null;
  generationMetadata?: Record<string, any> | null;
  /** Optional: pain/value the model actually picked (highlights the chosen chip). */
  painSelected?: string | null;
  valueSelected?: string | null;
  /** Optional: situation signals the matrix keyed on. */
  situationKeys?: string[] | null;
}

function titleCase(s: string): string {
  return s.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

// Map the raw matrix-source enum (both vocabularies) to a plain-English label +
// a tone so a genuine fallback reads differently from a legacy/untagged message.
//   metadata: db_matrix | db_matrix_cgt | role_fallback | default_fallback
//   column:   matrix | fallback | (null)
function sourceMeta(raw: string | null): { label: string; tone: "matrix" | "fallback" | "legacy" } {
  switch (raw) {
    case "db_matrix_cgt": return { label: "SSO Matrix · CGT", tone: "matrix" };
    case "db_matrix":
    case "matrix":        return { label: "SSO Matrix", tone: "matrix" };
    case "role_fallback": return { label: "Role fallback", tone: "fallback" };
    case "default_fallback":
    case "fallback":      return { label: "Generic fallback", tone: "fallback" };
    default:              return { label: "Not captured (legacy message)", tone: "legacy" };
  }
}

const SOURCE_TONE_CLASS: Record<"matrix" | "fallback" | "legacy", string> = {
  matrix: "bg-emerald-50 text-emerald-700 border-emerald-200",
  fallback: "bg-amber-50 text-amber-700 border-amber-200",
  legacy: "bg-muted text-muted-foreground",
};

function ConfigureLink({ block }: { block: ContextBlock }) {
  const href = contextLink(block);
  if (!href) return null;
  return (
    <Link
      to={href}
      className="inline-flex items-center gap-0.5 text-[11px] text-primary hover:underline"
      onClick={(e) => e.stopPropagation()}
    >
      Configure <ArrowUpRight className="h-3 w-3" />
    </Link>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <span className="text-xs text-muted-foreground">{label}</span>
      <div className="font-medium">{children}</div>
    </div>
  );
}

export function MessageContextPane(props: MessageContextPaneProps) {
  const {
    mode = "used",
    messagingVariant, messagingAngle, roleTypeUsed, accountCategory, matrixSource,
    enrichmentContext, messageType, deliveryChannel, senderName, contactName,
    contactTitle, accountName, generationMetadata,
    painSelected, valueSelected, situationKeys,
  } = props;

  const gm = generationMetadata ?? {};
  const isWillUse = mode === "willUse";

  // Pains / values / hook — read the live-agent seam first, then the batch shape.
  const painPoints =
    (gm.painPointsUsed as string[] | undefined) ??
    (enrichmentContext?.pain_points as string[] | undefined);
  const valueProps =
    (gm.valuePropsUsed as string[] | undefined) ??
    (enrichmentContext?.value_propositions as string[] | undefined);
  const hookType =
    (gm.hookType as string | undefined) ?? (enrichmentContext?.hook_type as string | undefined);
  const matchScore = (gm.matrixMatchScore ?? gm.matchScore) as number | undefined;
  const selectionRationale = gm.selectionRationale as string | undefined;
  const model = gm.model as string | undefined;
  const promptVersion = gm.prompt_version as string | undefined;
  const hydrated = gm.hydrated as boolean | undefined;
  const sigKeys = situationKeys ?? (gm.situationKeysUsed as string[] | undefined);
  const pickedPain = painSelected ?? (gm.painSelected as string | undefined);
  const pickedValue = valueSelected ?? (gm.valueSelected as string | undefined);

  const angle = messagingAngle ?? (gm.messagingAngle as string | undefined) ?? null;
  // Prefer the richer generation_metadata vocabulary (db_matrix_cgt) over the
  // legacy dedicated column ('matrix'); the EFs only write the metadata seam.
  const source = (gm.messagingSource as string | undefined) ?? matrixSource ?? null;
  const role = roleTypeUsed ?? (gm.marketingPersona as string | undefined) ?? (gm.roleTypeUsed as string | undefined) ?? null;
  const category = accountCategory ?? (gm.accountCategory as string | undefined) ?? null;

  const hasStrategy = !!(messagingVariant || angle || role || category || source || hookType || painPoints?.length || valueProps?.length || typeof matchScore === "number");
  const hasPositioning = !!(messageType || deliveryChannel || senderName || contactName || accountName || model);

  return (
    <div className="p-4 flex flex-col gap-4">
      {/* Header: honest "used" vs "will use" framing. */}
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold flex items-center gap-1.5">
          <Sparkles className="h-4 w-4 text-amber-500" />
          {isWillUse ? "Context the matrix will use" : "Context used to generate this"}
        </h3>
        {typeof hydrated === "boolean" && (
          <Badge variant="outline" className="text-[10px]">{hydrated ? "Hydrated" : "Base"}</Badge>
        )}
      </div>

      {isWillUse && (
        <div className="flex items-start gap-2 text-[11px] text-muted-foreground bg-muted/40 rounded-md px-3 py-2">
          <Info className="h-3.5 w-3.5 mt-0.5 shrink-0" />
          <span>These are the inputs the messaging matrix will draw on. The exact selection is finalized when the message is generated.</span>
        </div>
      )}

      {/* ── Strategy (messaging matrix) ─────────────────────────── */}
      {hasStrategy && (
        <Card className="border-dashed order-2">
          <CardHeader className="pb-2 pt-3 px-4">
            <CardTitle className="text-xs font-medium uppercase tracking-wide text-muted-foreground flex items-center justify-between gap-1.5">
              <span className="flex items-center gap-1.5">
                <Grid3X3 className="h-3.5 w-3.5" />
                Messaging Matrix
                {typeof matchScore === "number" && (
                  <Badge variant="outline" className="text-[10px]">match {matchScore}</Badge>
                )}
              </span>
              <ConfigureLink block="messaging_framework" />
            </CardTitle>
          </CardHeader>
          <CardContent className="px-4 pb-3 space-y-2.5">
            <div className="grid grid-cols-2 gap-x-3 gap-y-2 text-sm">
              {messagingVariant && <Field label="Variant">{titleCase(messagingVariant)}</Field>}
              {angle && <Field label="Angle">{angle}</Field>}
              {role && <Field label="Role Type">{titleCase(role)}</Field>}
              {category && <Field label="Account Category">{titleCase(category)}</Field>}
              {(source || !isWillUse) && (
                <Field label="Matrix Source">
                  <Badge variant="outline" className={`text-[11px] ${SOURCE_TONE_CLASS[sourceMeta(source).tone]}`}>
                    {sourceMeta(source).label}
                  </Badge>
                </Field>
              )}
              {hookType && (
                <Field label="Hook Type">
                  <span className="flex items-center gap-1"><Zap className="h-3 w-3 text-amber-500" />{hookType}</span>
                </Field>
              )}
            </div>

            {painPoints && painPoints.length > 0 && (
              <div>
                <span className="text-xs text-muted-foreground flex items-center gap-1 mb-1">
                  <Target className="h-3 w-3" /> {isWillUse ? "Pain Points Available" : "Pain Points Addressed"}
                  <span className="ml-auto"><ConfigureLink block="personas" /></span>
                </span>
                <div className="flex flex-wrap gap-1">
                  {painPoints.map((pp, i) => (
                    <Badge
                      key={i}
                      variant={pp === pickedPain ? "default" : "secondary"}
                      className="text-[11px]"
                      title={pp === pickedPain ? "Selected by the model" : undefined}
                    >
                      {pp}
                    </Badge>
                  ))}
                </div>
              </div>
            )}

            {valueProps && valueProps.length > 0 && (
              <div>
                <span className="text-xs text-muted-foreground flex items-center gap-1 mb-1">
                  <MessageCircle className="h-3 w-3" /> {isWillUse ? "Value Props Available" : "Value Props Used"}
                </span>
                <div className="flex flex-wrap gap-1">
                  {valueProps.map((vp, i) => (
                    <Badge
                      key={i}
                      variant="outline"
                      className={`text-[11px] ${vp === pickedValue ? "bg-blue-100 text-blue-800 border-blue-300 font-medium" : "bg-blue-50 text-blue-700 border-blue-200"}`}
                      title={vp === pickedValue ? "Selected by the model" : undefined}
                    >
                      {vp}
                    </Badge>
                  ))}
                </div>
              </div>
            )}

            {sigKeys && sigKeys.length > 0 && (
              <div>
                <span className="text-xs text-muted-foreground mb-1 block">Situation Signals</span>
                <div className="flex flex-wrap gap-1">
                  {sigKeys.map((k, i) => (
                    <Badge key={i} variant="outline" className="text-[10px] text-muted-foreground">{k}</Badge>
                  ))}
                </div>
              </div>
            )}

            {selectionRationale && (
              <div>
                <span className="text-xs text-muted-foreground">Why this pain + value</span>
                <p className="text-xs italic text-muted-foreground mt-0.5">{selectionRationale}</p>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* ── Positioning & delivery ──────────────────────────────── */}
      {hasPositioning && (
        <Card className="border-dashed order-1">
          <CardHeader className="pb-2 pt-3 px-4">
            <CardTitle className="text-xs font-medium uppercase tracking-wide text-muted-foreground flex items-center justify-between gap-1.5">
              <span className="flex items-center gap-1.5"><Layers className="h-3.5 w-3.5" />Positioning &amp; Delivery</span>
              <ConfigureLink block="account_scoring" />
            </CardTitle>
          </CardHeader>
          <CardContent className="px-4 pb-3">
            <div className="grid grid-cols-2 gap-x-3 gap-y-2 text-sm">
              {messageType && <Field label="Message Type">{titleCase(messageType)}</Field>}
              {deliveryChannel && (
                <Field label="Channel">
                  <Badge variant="outline" className="text-[11px]">
                    {deliveryChannel.toLowerCase().includes("email") ? "Email" : "LinkedIn"}
                  </Badge>
                </Field>
              )}
              {senderName && (
                <Field label="Sender">
                  <span className="flex items-center gap-1"><Send className="h-3 w-3" />{senderName}</span>
                </Field>
              )}
              {contactName && (
                <div>
                  <span className="text-xs text-muted-foreground flex items-center gap-1"><User className="h-3 w-3" />Recipient</span>
                  <div className="font-medium truncate">{contactName}</div>
                  {contactTitle && <div className="text-xs text-muted-foreground truncate">{contactTitle}</div>}
                </div>
              )}
              {accountName && (
                <Field label="Company"><span className="flex items-center gap-1"><Building2 className="h-3 w-3" />{accountName}</span></Field>
              )}
              {model && (
                <div>
                  <span className="text-xs text-muted-foreground">Model</span>
                  <div className="text-xs text-muted-foreground font-mono">{model}</div>
                </div>
              )}
              {promptVersion && (
                <div>
                  <span className="text-xs text-muted-foreground">Prompt</span>
                  <div className="text-xs text-muted-foreground font-mono">{promptVersion}</div>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {!hasStrategy && !isWillUse && (
        <div className="order-3 flex items-start gap-2 text-[11px] text-muted-foreground bg-muted/40 rounded-md px-3 py-2">
          <Info className="h-3.5 w-3.5 mt-0.5 shrink-0" />
          <span>No messaging-matrix metadata was captured for this message. Positioning is shown above.</span>
        </div>
      )}
    </div>
  );
}
